import { useReducer, useEffect, useState } from 'react'
import api, { Role, IEmployeeTableRecord, IHRLocation } from '@api'
import { Order, IPageInfo, IDepartment, IEmployee, IFloor, IBuilding } from '@api/types'
import { KeyValueFilter, ValueFilter } from '@api/employee/types'
import { uniqueId, get } from 'lodash'
import { IVariables } from '@api/employee/list'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  FILTER,
  ERROR,
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  employees: IEmployeeTableRecord[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface FilterAction {
  type: Actions.FILTER
  filters: Filters
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | FilterAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton

interface Sort {
  column: string
  order: Order
}

export interface Filters {
  cities: string[]
  keyValueFilters: { department: IDepartment | null }
  valueFilter: ValueFilter[]
  filters: {
    floor: IFloor | null
    building: IBuilding | null
    manager: IEmployee | null
    type: string | null
    role: Role | null
    query: string
    title: string | null
    geoLocation: IHRLocation | null
  }
}

interface State {
  reqId: string
  isLoading: boolean
  isSorting: boolean
  isPaging: boolean
  count: number
  items: IEmployeeTableRecord[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  filters: Filters
  first: number
}

export interface UseEmployeesHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  applyFilters: (filters: Filters) => void
  clearFilters: () => void
  reload: () => void
  selection: ISelection
}

let persistedState: State = {
  reqId: uniqueId('request'),
  isLoading: true,
  isSorting: false,
  isPaging: false,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  filters: {
    cities: [],
    valueFilter: [],
    keyValueFilters: {
      department: null,
    },
    filters: {
      manager: null,
      floor: null,
      building: null,
      type: null,
      role: null,
      query: '',
      title: null,
      geoLocation: null,
    },
  },
}

interface GetVariablesFunc {
  filters: Filters
  sort: Sort
  after?: string | null
  first?: number
}

export function getVariables({
  filters: filtersState,
  sort,
  after,
  first,
}: GetVariablesFunc): IVariables {
  let keyValueFilters: KeyValueFilter[] = []
  if (filtersState.keyValueFilters.department) {
    keyValueFilters = [
      {
        key: 'DepartmentId',
        value: filtersState.keyValueFilters.department.id,
      },
    ]
  }

  const filters: KeyValueFilter[] = []
  const managerId = get(filtersState, 'filters.manager.id')
  const floorId = get(filtersState, 'filters.floor.id')
  const buildingId = get(filtersState, 'filters.building.id')
  const geoLocationId = get(filtersState, 'filters.geoLocation.id')
  const type = filtersState.filters.type
  const role = filtersState.filters.role
  const query = filtersState.filters.query
  const title = filtersState.filters.title

  managerId && filters.push({ key: 'managerId', value: managerId })
  floorId && filters.push({ key: 'floorId', value: floorId })
  buildingId && filters.push({ key: 'buildingId', value: buildingId })
  geoLocationId && filters.push({ key: 'geoLocationId', value: geoLocationId })
  type && filters.push({ key: 'type', value: type })
  role && filters.push({ key: 'role', value: role })
  query && filters.push({ key: 'query', value: query })
  title && filters.push({ key: 'title', value: title })

  return {
    after,
    sortBy: sort.column,
    order: sort.order,
    ValueFilter: filtersState.valueFilter,
    keyValueFilters,
    filters,
    first,
  }
}

export default function useEmployees(): UseEmployeesHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const [shouldReload, setShouldReload] = useState(false)
  const selection = useSelection()

  const fetchEmployees =
    ({
      showSkeleton,
      loadAll,
      shouldClearSelection = true,
    }: {
      showSkeleton: boolean
      loadAll: boolean
      shouldClearSelection?: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        sort: state.sort,
        filters: state.filters,
        first: loadAll ? state.first : PAGE_SIZE,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      setShouldReload(false)

      api.employee.list(variables).then(({ edges, counters, pageInfo }) => {
        const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
        const itemIds: string[] = []

        const items = edges.map(({ node }) => {
          if (isAllItemsSelected) {
            itemIds.push(node.id)
          }

          return node
        })

        //clear selection outside filtered range
        if (shouldClearSelection) {
          selection.removeSelectionsByItems(items)
        }

        // select all items if the select all is checked
        if (isAllItemsSelected && items.length) {
          selection.addAll(itemIds)
        }

        dispatch({
          type: Actions.LOAD,
          count: counters.total,
          employees: items,
          pageInfo,
          reqId,
        })
      })
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(fetchEmployees({ showSkeleton: false, loadAll: false, shouldClearSelection: false }), [
    state.after,
  ])

  //api request without skeleton and with load all items in state.first
  useEffect(fetchEmployees({ showSkeleton: false, loadAll: true }), [state.sort, shouldReload])

  //api request with skeleton and with load all items in state.first
  useEffect(fetchEmployees({ showSkeleton: true, loadAll: true }), [state.filters])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column) => {
      dispatch({ type: Actions.SORT, column })
    },
    applyFilters: (filters) => {
      dispatch({ type: Actions.FILTER, filters })
    },
    clearFilters: () => {
      dispatch({
        type: Actions.FILTER,
        filters: {
          cities: [],
          valueFilter: [],
          keyValueFilters: {
            department: null,
          },
          filters: {
            manager: null,
            floor: null,
            building: null,
            type: null,
            role: null,
            query: '',
            title: null,
            geoLocation: null,
          },
        },
      })
    },
    reload: () => setShouldReload(true),
    selection: selection,
  }
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: true,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        isLoading: false,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId
        ? { ...state, isLoading: false, isSorting: false, isPaging: false }
        : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            isLoading: false,
            isSorting: false,
            isPaging: false,
            count: action.count,
            items: state.isPaging ? [...state.items, ...action.employees] : action.employees,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        isSorting: true,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.FILTER:
      return {
        ...state,
        filters: action.filters,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }

    default:
      return state
  }
}
